import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement, DoorElement, WindowElement, BeamElement, BIMElement } from '../../core/types';
import { createTranslatedWallCopy } from '../../utils/WallGeometryUtils';
import { copySceneObjects } from '../../core/scene/editRouter';

/**
 * Copy Tool - BIM 2025 Style
 * 
 * Creates copies of selected elements at new locations.
 * Supports:
 * - Single and multi-element selection
 * - Multiple copy mode (continuous)
 * - Constraint mode (horizontal/vertical)
 * 
 * Workflow:
 * 1. Select elements (or pre-select)
 * 2. Click base point
 * 3. Click destination(s)
 * 4. Press Escape to finish
 */
export class CopyToolClass extends BaseTool {
    id = 'COPY';
    name = 'Copy';
    cursor = 'copy';

    private basePoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private constrainMode: 'none' | 'horizontal' | 'vertical' = 'none';
    private selectedElements: BIMElement[] = [];
    // B4 cross-workbench: selection ids owned by OTHER workbenches.
    private foreignIds: string[] = [];
    private multipleCopyMode: boolean = true; // By default, allow multiple copies

    activate(context: any): void {
        super.activate(context);
        this.loadSelection();

        if (this.selectedElements.length === 0) {
            console.log('Copy: No elements selected. Click to select elements first.');
        } else {
            console.log('Copy: Selected', this.selectedElements.length, 'elements. Click base point.');
        }
    }

    private loadSelection(): void {
        if (!this.context) return;

        const selectedIds = this.context.appStore.selectedIds || [];
        this.selectedElements = [];
        this.foreignIds = [];

        for (const id of selectedIds) {
            const element = this.context.bimStore.project.elements.get(id);
            if (element) {
                this.selectedElements.push(element);
            } else {
                this.foreignIds.push(id);
            }
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        // If no selection, try to select clicked element
        if (this.selectedElements.length === 0) {
            const id = this.findElementIdAtPoint(event);
            if (id) {
                this.selectElement(id);
                this.loadSelection();
            }
            return;
        }

        if (!this.basePoint) {
            // First click: Base point
            this.basePoint = snappedPoint.clone();
            console.log('Copy: Base point set', this.basePoint);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                start: this.basePoint,
                current: this.basePoint
            });
        } else {
            // Subsequent clicks: Create copies
            const destination = this.applyConstraint(snappedPoint);
            const translation = destination.clone().sub(this.basePoint);

            this.copyElements(translation);

            if (!this.multipleCopyMode) {
                // Single copy mode - reset and exit
                this.reset();
                this.context.appStore.setActiveTool('select');
            }
            // In multiple copy mode, continue allowing more copies
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.basePoint) return;

        const point = this.getIntersection(event);
        if (!point) return;

        this.currentPoint = this.applyConstraint(this.snapToGrid(point));

        this.context.appStore.setToolPreview({
            active: true,
            toolId: this.id,
            start: this.basePoint,
            current: this.currentPoint
        });
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        // Constraint modes
        if (event.shiftKey) {
            this.constrainMode = 'horizontal';
        }

        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }

        // Enter to finish multiple copy
        if (event.key === 'Enter') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private applyConstraint(point: THREE.Vector3): THREE.Vector3 {
        if (!this.basePoint || this.constrainMode === 'none') return point;

        const delta = point.clone().sub(this.basePoint);

        if (this.constrainMode === 'horizontal') {
            if (Math.abs(delta.x) > Math.abs(delta.y)) {
                return new THREE.Vector3(this.basePoint.x + delta.x, this.basePoint.y, point.z);
            } else {
                return new THREE.Vector3(this.basePoint.x, this.basePoint.y + delta.y, point.z);
            }
        }

        return point;
    }

    private copyElements(translation: THREE.Vector3): void {
        if (!this.context) return;

        const newIds: string[] = [];

        for (const element of this.selectedElements) {
            const newId = this.copyElement(element, translation);
            if (newId) newIds.push(newId);
        }

        // B4 cross-workbench: foreign citizens copy through the edit router.
        if (this.foreignIds.length > 0) {
            newIds.push(...copySceneObjects(
                this.foreignIds,
                new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z),
                'Copy'
            ));
        }

        console.log('Copy: Created', newIds.length, 'copies');
    }

    private copyElement(element: BIMElement, translation: THREE.Vector3): string | null {
        if (!this.context) return null;

        switch (element.category) {
            case 'Walls':
                return this.copyWall(element as WallElement, translation);
            case 'Doors':
                return this.copyDoor(element as DoorElement, translation);
            case 'Windows':
                return this.copyWindow(element as WindowElement, translation);
            case 'Structural Framing':
                return this.copyBeam(element as BeamElement, translation);
            case 'Structural Columns':
                return this.copyColumn(element as any, translation);
            default:
                return this.copyGenericElement(element, translation);
        }
    }

    private copyWall(wall: WallElement, translation: THREE.Vector3): string {
        // Copies leave their joins behind and get fresh render geometry
        // (stale layer profiles would render the copy at the old location).
        const newWall = createTranslatedWallCopy(wall, translation);
        this.context!.bimStore.addElement(newWall);
        return newWall.id;
    }

    private copyDoor(door: DoorElement, _translation: THREE.Vector3): string {
        // For hosted elements, we need to find or create a new host
        // Simplified: just copy without host
        const newDoor: DoorElement = {
            ...door,
            id: THREE.MathUtils.generateUUID(),
            constraints: []
        };

        this.context!.bimStore.addElement(newDoor);
        return newDoor.id;
    }

    private copyWindow(window: WindowElement, _translation: THREE.Vector3): string {
        const newWindow: WindowElement = {
            ...window,
            id: THREE.MathUtils.generateUUID(),
            constraints: []
        };

        this.context!.bimStore.addElement(newWindow);
        return newWindow.id;
    }

    private copyBeam(beam: BeamElement, translation: THREE.Vector3): string {
        const start = new THREE.Vector3(...beam.geometryData.start).add(translation);
        const end = new THREE.Vector3(...beam.geometryData.end).add(translation);

        const newBeam: BeamElement = {
            ...beam,
            id: THREE.MathUtils.generateUUID(),
            geometryData: {
                ...beam.geometryData,
                start: [start.x, start.y, start.z],
                end: [end.x, end.y, end.z]
            },
            constraints: []
        };

        this.context!.bimStore.addElement(newBeam);
        return newBeam.id;
    }

    private copyColumn(column: any, translation: THREE.Vector3): string {
        const newColumn = {
            ...column,
            id: THREE.MathUtils.generateUUID(),
            constraints: []
        };

        // Update position if available
        if (column.geometryData?.position) {
            const pos = new THREE.Vector3(...column.geometryData.position).add(translation);
            newColumn.geometryData = {
                ...column.geometryData,
                position: [pos.x, pos.y, pos.z]
            };
        }

        this.context!.bimStore.addElement(newColumn);
        return newColumn.id;
    }

    private copyGenericElement(element: BIMElement, _translation: THREE.Vector3): string {
        const newElement = {
            ...element,
            id: THREE.MathUtils.generateUUID(),
            constraints: []
        };

        this.context!.bimStore.addElement(newElement);
        return newElement.id;
    }

    private reset(): void {
        this.basePoint = null;
        this.currentPoint = null;
        this.constrainMode = 'none';
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
        this.selectedElements = [];
    }
}
